import { useCallback, useEffect } from "react";

import { Button, Link } from "@shapr3d/frontend-lib";
import classNames from "classnames";

import { eduUrl } from "../../../config";
import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import { ElementClickAction } from "../../../lib/TrackingAdapter";
import { SubscriptionPeriod } from "../../../types";
import PriceSection from "../PriceSection/PriceSection";

import style from "./PriceCard.module.css";

interface PriceCardProps extends React.HTMLProps<HTMLDivElement> {
  featured?: boolean;
  planType: string;
  price?: PriceProps;
  description: string;
  highlight?: string;
  featuresTitle: string;
  features: string[];
  button?: {
    onClick?: (period: SubscriptionPeriod) => void;
    href?: (period: SubscriptionPeriod) => string;
    text: string;
    trackingAction?: ElementClickAction;
  };
  isLoadingPrice?: boolean;
  periodPrefix?: string;
  selectedPeriod?: SubscriptionPeriod;
  shouldPostPurchaseURL?: boolean;
}

export interface PriceProps {
  currencySymbol: string;
  monthly: number;
  yearly: number;
  monthlyDiscountPercent?: number;
  yearlyDiscountPercent?: number;
  availablePeriods: SubscriptionPeriod[];
}

const PriceCard: React.FC<PriceCardProps> = ({
  className,
  featured,
  planType,
  description,
  price,
  highlight,
  featuresTitle,
  features,
  button,
  isLoadingPrice,
  periodPrefix,
  selectedPeriod,
  shouldPostPurchaseURL,
}) => {
  const { t, m } = useTranslation();

  const initialSubscriptionPeriod =
    price?.availablePeriods.length === 1 ? price.availablePeriods[0] : SubscriptionPeriod.Yearly;

  const tracker = useTracker();
  const handleButtonClick = useCallback(() => {
    tracker.trackButtonClick({
      elementId: "pricing_card_button_" + button?.trackingAction,
      action: button?.trackingAction,
      location: "pricing_card",
      ...(button?.trackingAction === ElementClickAction.START_TRIAL_FLOW && {
        details: selectedPeriod || initialSubscriptionPeriod,
      }),
    });

    if (button?.href) {
      window.open(
        button.href(selectedPeriod || initialSubscriptionPeriod),
        "_blank",
        "noopener,noreferrer"
      );
    }
    button?.onClick?.(selectedPeriod || initialSubscriptionPeriod);
  }, [button, selectedPeriod, initialSubscriptionPeriod, tracker]);

  useEffect(() => {
    if (shouldPostPurchaseURL) {
      const period = selectedPeriod || initialSubscriptionPeriod;
      const purchaseURL = button?.href?.(period) ?? "";
      if (purchaseURL) {
        window.parent.postMessage({ purchaseURL, period }, "*");
      }
    }
  }, [button, selectedPeriod, initialSubscriptionPeriod, shouldPostPurchaseURL]);

  return (
    <div className={classNames(className, style.priceCard, featured && style.featured)}>
      <h6 className={style.type}>{planType}</h6>
      <h4 className={style.description}>{description}</h4>
      {highlight && (
        <h4 className={style.highlight}>
          {highlight}
          <span style={{ visibility: "hidden" }}>.</span>
        </h4>
      )}
      <div className={style.priceSection}>
        <PriceSection
          price={price}
          selectedPeriod={selectedPeriod || initialSubscriptionPeriod}
          periodPrefix={periodPrefix}
          isLoadingPrice={isLoadingPrice}
        />
      </div>
      <hr className={style.separator}></hr>
      <div className={style.features}>
        <span className={style.featuresTitle}>{featuresTitle}</span>

        <ul className={style.featureList}>
          {features.map((item) => (
            <li key={item} className={style.featureListItem}>
              {item}
            </li>
          ))}
        </ul>
      </div>
      {button && (
        <div className={style.buttonContainer}>
          {!button.href && !button.onClick ? (
            <div className={style.currentPlan}>{button.text}</div>
          ) : (
            <Button
              className={style.button}
              variant={!featured ? "grayscale" : "primary"}
              onClick={handleButtonClick}
            >
              {button.text}
            </Button>
          )}
        </div>
      )}
      {featured && (
        <Link
          className={style.eduLink}
          href={eduUrl}
          onClick={() => {
            tracker.trackButtonClick({
              elementId: "pricing_card_edu_button",
              action: ElementClickAction.START_EDU_FLOW,
              location: "pricing_card",
            });
          }}
          target="_blank"
        >
          {t(m.Purchase.FreeForStudents)}
        </Link>
      )}
    </div>
  );
};

export default PriceCard;
